import {
  BackgroundCheckRequestStatus,
  IBackgroundCheckRequestResponse
} from "@wingspanhq/background-checks/dist/lib/interfaces";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import {
  getRequirementDefinitionDataItemProps,
  getRequirementDefinitionTableCellProps
} from "../../../../../RequirementDefinitions/utils";
import { selectorRequirementEngagementsSize } from "../../../../selectors/selectorRequirementEngagementsSize";
import {
  CellsConfig,
  ContextItem,
  RequirementAction,
  RequirementTableCallback
} from "../../../../types";
import { useBackgroundChecksStartModal } from "../../modals/useBackgroundChecksStartModal";
import { getBackgroundCheckPayeeRequirementStatus } from "../helpers/getBackgroundCheckPayeeRequirementStatus";
import { getBackgroundCheckProgressSteps } from "../helpers/getBackgroundCheckProgressSteps";
import { getBackgroundChecksRequirementName } from "../helpers/getBackgroundChecksRequirementName";
import { payerBackgroundCheckRenderCardDescription } from "./payerBackgroundCheckRenderCardDescription";

export const usePayerBackgroundChecksConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  IRequirementResponse,
  ContextItem,
  IBackgroundCheckRequestResponse | undefined
> => {
  const backgroundChecksStartModal = useBackgroundChecksStartModal();

  const getCheckAction = (
    requirement: IRequirementResponse,
    context: ContextItem,
    backgroundChecksRequest: IBackgroundCheckRequestResponse | undefined
  ) => {
    if (
      backgroundChecksRequest?.vendorMetadata.applicationUrl &&
      backgroundChecksRequest.status === BackgroundCheckRequestStatus.Pending
    ) {
      return async () => {
        await backgroundChecksStartModal.open({
          backgroundCheckRequestId:
            backgroundChecksRequest.backgroundCheckRequestId
        });
      };
    }

    return undefined;
  };

  return {
    isActionRequired: (requirement, context, backgroundChecksRequest) => {
      return !!(
        backgroundChecksRequest?.vendorMetadata.applicationUrl &&
        backgroundChecksRequest.status === BackgroundCheckRequestStatus.Pending
      );
    },
    requirementDetailsProgressWidget: (
      requirement,
      context,
      backgroundChecksRequest
    ) => {
      return backgroundChecksRequest?.status
        ? getBackgroundCheckProgressSteps(backgroundChecksRequest.status)
        : undefined;
    },
    requirementStatusAlert: (requirement, context, backgroundChecksRequest) => {
      if (
        backgroundChecksRequest?.status === BackgroundCheckRequestStatus.Pending
      ) {
        return {
          theme: "warning",
          title: "Action required",
          children:
            "Complete the requirement in order to be eligible to receive payments from your client.",
          icon: "alert"
        };
      }
    },
    requirementName: getBackgroundChecksRequirementName,
    requirementBasicInformation: (
      requirement,
      context,
      backgroundChecksRequest
    ) => {
      const status = getBackgroundCheckPayeeRequirementStatus(
        requirement,
        context,
        backgroundChecksRequest
      );

      return [
        getRequirementDefinitionDataItemProps({
          type: requirement.requirementType
        }),
        {
          label: "Engagements",
          value: selectorRequirementEngagementsSize(requirement)
        },
        {
          label: {
            text: "Status"
          },
          value: "",
          hideValue: true,
          pill: status.pill,
          secondaryText: status.secondaryText
        }
      ];
    },
    requirementDetails: (requirement, context, backgroundChecksRequest) => {
      const backgroundCheckAction = getCheckAction(
        requirement,
        context,
        backgroundChecksRequest
      );

      return {
        header: {
          label: {
            text: getBackgroundChecksRequirementName(
              requirement,
              context,
              backgroundChecksRequest
            )
          }
        },
        children: payerBackgroundCheckRenderCardDescription(
          requirement,
          backgroundChecksRequest
        ),
        data: [],
        actions: backgroundCheckAction
          ? {
              alignment: "fill",
              buttons: [
                {
                  label: "Start background check",
                  onAsyncClick: backgroundCheckAction,
                  kind: "Primary",
                  visible: !!backgroundCheckAction
                }
              ]
            }
          : undefined
      };
    },
    toolbarActions: (requirement, context, backgroundChecksRequest) => {
      const actions: RequirementAction[] = [];

      const backgroundCheckAction = getCheckAction(
        requirement,
        context,
        backgroundChecksRequest
      );

      if (backgroundCheckAction) {
        actions.push({
          label: "Start background check",
          onAsyncClick: backgroundCheckAction,
          buttonKind: "Primary",
          icon: "open-tab",
          buttonIcon: null,
          mainAction: true
        });
      }

      return actions;
    },
    nameCell: (requirement, context, backgroundChecksRequest) => {
      return {
        text: getBackgroundChecksRequirementName(
          requirement,
          context,
          backgroundChecksRequest
        )
      };
    },
    typeCell: requirement =>
      getRequirementDefinitionTableCellProps({
        type: requirement.requirementType
      }),
    statusCell: (requirement, context, backgroundChecksRequest) => {
      return getBackgroundCheckPayeeRequirementStatus(
        requirement,
        context,
        backgroundChecksRequest
      );
    }
  };
};
